import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader2, Upload } from 'lucide-react';
import React, { useRef, useState } from 'react';

interface FileUploadProps {
  onFilesSelect: (files: File[]) => void;
  isUploading?: boolean;
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
  label?: string;
  showHelpText?: boolean;
  showDropZone?: boolean;
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesSelect,
  isUploading = false,
  disabled = false,
  accept = '.pdf,.doc,.docx,.png,.jpg,.jpeg',
  multiple = true,
  label,
  showHelpText = true,
  showDropZone = true,
  className = '',
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  /**
   * Formats the accept string into a readable list of file types
   */
  const getFormattedAcceptTypes = (): string => {
    if (!accept) return '';
    return accept
      .split(',')
      .map(type => type.trim().replace('.', '').toUpperCase())
      .join(', ');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Convert FileList to File array and call the callback
      const filesArray = Array.from(files);
      onFilesSelect(filesArray);
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (disabled || isUploading) return;

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      onFilesSelect(filesArray);
    }
  };

  // Shared upload button component
  const uploadButton = (
    <Button
      type='button'
      onClick={handleUploadButtonClick}
      disabled={disabled || isUploading}
      className={`gap-2 ${showDropZone ? 'pointer-events-none' : ''}`}
      variant={showDropZone && isDragOver ? 'default' : 'outline'}
      size='default'
    >
      {isUploading ? (
        <Loader2 className='h-4 w-4 animate-spin' />
      ) : (
        <Upload className='h-4 w-4' />
      )}
      {t('common.fileManagement.upload')}
    </Button>
  );

  // Shared help text component
  const helpText = showHelpText && (
    <div className='text-xs text-muted-foreground space-y-1'>
      <p>{t('common.fileManagement.maxFileSize')}</p>
      <p>
        {t('common.fileManagement.allowedTypesLabel')}:{' '}
        {getFormattedAcceptTypes()}
      </p>
      {showDropZone && (
        <p className='text-primary/70 font-medium'>
          {t('common.fileManagement.dragDropText')}
        </p>
      )}
    </div>
  );

  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <Label htmlFor='file-upload' className='text-sm font-medium'>
          {label}
        </Label>
      )}

      {/* Hidden file input */}
      <Input
        ref={fileInputRef}
        id='file-upload'
        type='file'
        multiple={multiple}
        accept={accept}
        onChange={handleFileSelect}
        disabled={disabled || isUploading}
        className='hidden'
      />

      {showDropZone ? (
        /* Drop zone */
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-4 text-center transition-colors
            ${
              isDragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }
            ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={
            !disabled && !isUploading ? handleUploadButtonClick : undefined
          }
        >
          <div className='flex flex-col items-center justify-center space-y-4'>
            {uploadButton}
            {helpText}
          </div>
        </div>
      ) : (
        /* Simple upload button */
        <div className='space-y-3'>
          {uploadButton}
          {helpText}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
